import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import LanguageToggle from '../components/LanguageToggle';
import WindowsInstallationGuide from '../components/WindowsInstallationGuide';
import { getDownloadTranslation, detectLanguage } from './downloadTranslations';

const DownloadPage: React.FC = () => {
  const navigate = useNavigate();
  const [userOS, setUserOS] = useState<'windows' | 'macos' | 'unknown'>('unknown');
  const [windowsVersion, setWindowsVersion] = useState<any>(null);
  const [macosVersion, setMacosVersion] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [autoDownloadUrl, setAutoDownloadUrl] = useState<string | null>(null);
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getDownloadTranslation('en'));
  
  // Initialize language
  useEffect(() => {
    const detectedLang = detectLanguage();
    setLang(detectedLang);
    setT(getDownloadTranslation(detectedLang));
  }, []);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getDownloadTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  // Detect user OS
  useEffect(() => {
    const detectOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const platformStr = window.navigator.platform?.toLowerCase() || '';
      
      if (userAgent.includes('win') || platformStr.includes('win')) {
        setUserOS('windows');
      } else if (userAgent.includes('mac') || platformStr.includes('mac')) {
        setUserOS('macos');
      } else {
        setUserOS('unknown');
      }
    };
    
    detectOS();
  }, []);
  
  // Fetch latest versions from Supabase
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        // Determine environment based on current URL
        const isDev = window.location.hostname.includes('dev.');
        const environment = isDev ? 'development' : 'production';
        
        // Fetch Windows version
        const { data: winData, error: winError } = await supabase
          .rpc('get_latest_app_version', {
            p_platform: 'windows',
            p_environment: environment
          });
        
        if (!winError && winData && winData.length > 0) {
          setWindowsVersion(winData[0]);
        }
        
        // Fetch macOS version
        const { data: macData, error: macError } = await supabase
          .rpc('get_latest_app_version', {
            p_platform: 'macos',
            p_environment: environment
          });
        
        if (!macError && macData && macData.length > 0) {
          setMacosVersion(macData[0]);
        }
        
        // Set auto download URL based on detected OS
        if (userOS === 'windows' && winData && winData.length > 0) {
          setAutoDownloadUrl(winData[0].download_url_primary);
        } else if (userOS === 'macos' && macData && macData.length > 0) {
          // For macOS, prefer PKG (secondary) over DMG (primary)
          setAutoDownloadUrl(macData[0].download_url_secondary || macData[0].download_url_primary);
        }
      } catch (error) {
        console.error('Error fetching versions:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVersions();
  }, [userOS]);
  
  // Record download statistics
  const handleDownload = async (platform: string, version: string, downloadType: string, url: string) => {
    try {
      // Record download in statistics
      await supabase.rpc('record_app_download', {
        p_platform: platform,
        p_version: version,
        p_download_type: downloadType,
        p_user_agent: window.navigator.userAgent,
        p_referrer: document.referrer || null
      });
    } catch (error) {
      console.error('Error recording download:', error);
    }
    
    // Open download URL
    window.open(url, '_blank');
  };

  // Build downloads array from fetched versions
  const downloads = [];
  
  if (windowsVersion) {
    downloads.push({
      platform: t.platform.windows,
      platformKey: 'windows',
      icon: '🪟',
      version: windowsVersion.version,
      size: windowsVersion.file_size_primary || '37 MB',
      requirements: windowsVersion.min_os_version || 'Windows 10 이상',
      downloadUrl: windowsVersion.download_url_primary,
      isRecommended: userOS === 'windows'
    });
  }
  
  if (macosVersion) {
    downloads.push({
      platform: t.platform.macos,
      platformKey: 'macos',
      icon: '🍎',
      version: macosVersion.version,
      size: macosVersion.file_size_primary || '80 MB',
      requirements: macosVersion.min_os_version || 'macOS 10.14 이상',
      primaryUrl: macosVersion.download_url_primary,
      secondaryUrl: macosVersion.download_url_secondary,
      primarySize: macosVersion.file_size_primary || '80 MB',
      secondarySize: macosVersion.file_size_secondary || '80 MB',
      isRecommended: userOS === 'macos'
    });
  }

  const features = [
    {
      icon: '💬',
      title: t.features.items.realtime.title,
      description: t.features.items.realtime.description
    },
    {
      icon: '🌐',
      title: t.features.items.translation.title,
      description: t.features.items.translation.description
    },
    {
      icon: '📹',
      title: t.features.items.video.title,
      description: t.features.items.video.description
    },
    {
      icon: '🔒',
      title: t.features.items.secure.title,
      description: t.features.items.secure.description
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* 헤더 */}
      <header className="bg-white/90 backdrop-blur-xl shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button onClick={() => navigate('/')} className="flex items-center group">
                <img 
                  src="/aivuebiz_logo.png" 
                  alt="AivueBiz Logo" 
                  className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
                />
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageToggle currentLang={lang} onLanguageChange={handleLanguageChange} />
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t.header.login}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 히어로 섹션 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-4">
            {t.hero.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.hero.subtitle}
          </p>
        </div>

        {/* Auto-download banner for detected OS */}
        {autoDownloadUrl && userOS !== 'unknown' && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-1">
                    {userOS === 'windows' ? t.platform.windows : t.platform.macos} {lang === 'ko' ? '용 앱이 감지되었습니다' : ' app detected'}
                  </h3>
                  <p className="text-primary-100">
                    {t.download.autoDownload}
                  </p>
                </div>
                <button
                  onClick={() => {
                    const version = userOS === 'windows' ? windowsVersion : macosVersion;
                    if (version) {
                      // For macOS, download PKG (secondary), for Windows download EXE (primary)
                      const downloadType = userOS === 'macos' ? 'secondary' : 'primary';
                      handleDownload(userOS, version.version, downloadType, autoDownloadUrl);
                    }
                  }}
                  className="px-6 py-3 bg-white text-primary-700 rounded-xl font-semibold hover:bg-primary-50 transition-all"
                >
                  {t.download.downloadNow}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Loading state */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-gray-600">Loading version information...</p>
          </div>
        ) : (
          /* 다운로드 카드 */
          <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
            {downloads.map((item) => (
              <div 
                key={item.platform} 
                className={`bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
                  item.isRecommended ? 'ring-2 ring-primary-600 ring-offset-2' : ''
                }`}
              >
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{item.icon}</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {item.platform}
                  {item.isRecommended && (
                    <span className="ml-2 text-sm px-2 py-1 bg-primary-100 text-primary-700 rounded-full">
                      Recommended
                    </span>
                  )}
                </h2>
                <p className="text-gray-600">Version {item.version}</p>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">File Size</span>
                  <span className="text-gray-900 font-medium">{item.size}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t.requirements.title}</span>
                  <span className="text-gray-900 font-medium">{item.requirements}</span>
                </div>
              </div>

              {/* Windows: Single download button */}
              {item.platformKey === 'windows' && (
                <button
                  onClick={() => handleDownload(item.platformKey, item.version, 'primary', item.downloadUrl)}
                  className="block w-full py-3 px-6 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold text-center hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  {t.download.exe}
                </button>
              )}
              
              {/* macOS: PKG and DMG options (PKG prioritized) */}
              {item.platformKey === 'macos' && (
                <div className="space-y-3">
                  {item.secondaryUrl && (
                    <button
                      onClick={() => handleDownload(item.platformKey, item.version, 'secondary', item.secondaryUrl)}
                      className="block w-full py-3 px-6 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold text-center hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      {t.download.pkg} ({item.secondarySize})
                      <span className="text-xs block mt-1 opacity-90">{t.download.recommended}</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleDownload(item.platformKey, item.version, 'primary', item.primaryUrl)}
                    className="block w-full py-3 px-6 bg-white border-2 border-primary-600 text-primary-600 rounded-xl font-semibold text-center hover:bg-primary-50 transition-all duration-300"
                  >
                    {t.download.dmg} ({item.primarySize})
                    <span className="text-xs block mt-1 opacity-75">{t.download.installer}</span>
                  </button>
                </div>
              )}
              </div>
            ))}
          </div>
        )}

        {/* 기능 소개 */}
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t.features.title}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-3">{feature.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 설치 가이드 */}
        <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.installGuide.title}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                {t.installGuide.steps.download.number}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t.installGuide.steps.download.title}</h3>
                <p className="text-sm text-gray-600">{t.installGuide.steps.download.description}</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                {t.installGuide.steps.install.number}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t.installGuide.steps.install.title}</h3>
                <p className="text-sm text-gray-600">{t.installGuide.steps.install.description}</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                {t.installGuide.steps.start.number}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t.installGuide.steps.start.title}</h3>
                <p className="text-sm text-gray-600">{t.installGuide.steps.start.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* macOS Installation Guide - Only show for macOS users */}
        {userOS === 'macos' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12 max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              {lang === 'ko' ? 'macOS 설치 상세 가이드' : 'macOS Installation Details'}
            </h2>
            
            {/* PKG Installation Guide */}
            <div className="mb-10">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">
                  PKG
                </div>
                <h3 className="ml-4 text-xl font-bold text-gray-900">
                  {lang === 'ko' ? 'PKG 파일로 설치하기 (권장)' : 'Install with PKG file (Recommended)'}
                </h3>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="order-2 md:order-1">
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          1
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-900 font-medium">
                            {lang === 'ko' 
                              ? 'aivuebiz_[version].pkg 파일을 다운로드합니다.'
                              : 'Download aivuebiz_[version].pkg file.'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          2
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-900 font-medium">
                            {lang === 'ko' 
                              ? '다운로드한 PKG 파일을 더블클릭합니다.'
                              : 'Double-click the downloaded PKG file.'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          3
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-900 font-medium">
                            {lang === 'ko' 
                              ? '설치 마법사의 안내에 따라 설치를 완료합니다.'
                              : 'Follow the installation wizard to complete the setup.'}
                          </p>
                          <p className="text-gray-600 text-sm mt-1">
                            {lang === 'ko' 
                              ? '(관리자 암호가 필요할 수 있습니다)'
                              : '(Administrator password may be required)'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="order-1 md:order-2">
                    <div className="relative group overflow-hidden rounded-lg shadow-xl bg-gradient-to-br from-gray-100 to-gray-200 aspect-video flex items-center justify-center">
                      <img 
                        src="/img/pkg.png" 
                        alt="PKG Installation" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden flex-col items-center justify-center p-8">
                        <div className="text-6xl mb-4">📦</div>
                        <p className="text-gray-600 text-center">
                          {lang === 'ko' ? 'PKG 설치 화면' : 'PKG Installation Screen'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* DMG Installation Guide */}
            <div>
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">
                  DMG
                </div>
                <h3 className="ml-4 text-xl font-bold text-gray-900">
                  {lang === 'ko' ? 'DMG 파일로 설치하기' : 'Install with DMG file'}
                </h3>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          1
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-900 font-medium">
                            {lang === 'ko' 
                              ? 'aivuebiz_[version].dmg 파일을 다운로드합니다.'
                              : 'Download aivuebiz_[version].dmg file.'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          2
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-900 font-medium">
                            {lang === 'ko' 
                              ? '다운로드한 DMG 파일을 더블클릭합니다.'
                              : 'Double-click the downloaded DMG file.'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          3
                        </div>
                        <div className="ml-3">
                          <p className="text-gray-900 font-medium">
                            {lang === 'ko' 
                              ? 'AivueBiz 앱 아이콘을 Applications 폴더로 드래그합니다.'
                              : 'Drag the AivueBiz app icon to the Applications folder.'}
                          </p>
                          <p className="text-gray-600 text-sm mt-1">
                            {lang === 'ko' 
                              ? '(화살표 방향으로 드래그 앤 드롭)'
                              : '(Drag and drop following the arrow)'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="relative group overflow-hidden rounded-lg shadow-xl bg-gradient-to-br from-gray-100 to-gray-200 aspect-video flex items-center justify-center">
                      <img 
                        src="/img/dmg.png" 
                        alt="DMG Installation" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden flex-col items-center justify-center p-8">
                        <div className="text-6xl mb-4">💿</div>
                        <p className="text-gray-600 text-center">
                          {lang === 'ko' ? 'DMG 설치 화면' : 'DMG Installation Screen'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Additional Tips */}
            <div className="mt-8 space-y-3">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">💡</span>
                  <div>
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">
                        {lang === 'ko' ? '팁: ' : 'Tip: '}
                      </span>
                      {lang === 'ko' 
                        ? '첫 실행 시 "개발자를 확인할 수 없습니다" 메시지가 나타나면, 시스템 환경설정 > 보안 및 개인정보 보호에서 "확인 없이 열기"를 클릭하세요.'
                        : 'If you see "Cannot verify developer" on first launch, go to System Preferences > Security & Privacy and click "Open Anyway".'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">✅</span>
                  <div>
                    <p className="text-sm text-green-800">
                      <span className="font-semibold">
                        {lang === 'ko' ? '보안: ' : 'Security: '}
                      </span>
                      {lang === 'ko' 
                        ? 'Apple Developer로 공증인증(Notarized) 된 앱이니 안심하셔도 됩니다.'
                        : 'This app is notarized by Apple Developer, so you can use it with confidence.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Windows Installation Guide - Only show for Windows users */}
        {userOS === 'windows' && (
          <WindowsInstallationGuide lang={lang} />
        )}

        {/* FAQ 섹션 */}
        <div className="bg-white rounded-2xl shadow-sm p-8" id="reset-password">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.faq.title}</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t.faq.items.forgotPassword.question}</h3>
              <p className="text-gray-600">
                {t.faq.items.forgotPassword.answer}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t.faq.items.webFeatures.question}</h3>
              <p className="text-gray-600">
                {t.faq.items.webFeatures.answer}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t.faq.items.howToSignup.question}</h3>
              <p className="text-gray-600">
                {t.faq.items.howToSignup.answer}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t.faq.items.systemRequirements.question}</h3>
              <p className="text-gray-600">
                {t.faq.items.systemRequirements.answer}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t.faq.items.mobileApp.question}</h3>
              <p className="text-gray-600">
                {t.faq.items.mobileApp.answer}
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            {/* 도움이 필요하신가요? */}
          </p>
          {/* <a
            href="/support"
            className="inline-flex items-center px-6 py-3 border-2 border-primary-600 text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-300"
          >
            고객지원 센터
          </a> */}
        </div>
      </main>
    </div>
  );
};

export default DownloadPage;